import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

const READINESS_TONE = (score) =>
  score >= 80 ? "success" : score >= 50 ? "warn" : "danger";

export default function FoundationOverview() {
  const [brands, setBrands] = useState(null);
  const [products, setProducts] = useState(null);
  const [audiences, setAudiences] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [b, p, a, r] = await Promise.all([
          api.get("/brands"),
          api.get("/products"),
          api.get("/audiences"),
          api.get("/intelligence/readiness").catch(() => ({ data: null })),
        ]);
        if (!alive) return;
        setBrands(b.data?.brands || []);
        setProducts(p.data?.products || []);
        setAudiences(a.data?.audiences || []);
        setReadiness(r?.data || null);
      } catch (e) {
        if (alive) setErr(formatApiError(e));
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <Shell>
      <div className="bo-page" data-testid="foundation-overview-page">
        <header className="bo-page-header">
          <div>
            <div className="bo-eyebrow">FOUNDATION</div>
            <h1 data-testid="foundation-overview-title">Brand · Product · Audience</h1>
            <p className="bo-page-lede">
              The three brains that ground every campaign. Keep them fresh and every downstream artifact stays sharp.
            </p>
          </div>
        </header>

        {err && <ErrorBanner error={err} testId="foundation-overview-error" />}

        <div className="bo-grid-3" data-testid="foundation-overview-cards">
          {/* Brand */}
          <section className="bo-card" data-testid="foundation-brand-card">
            <header className="bo-card-header">
              <h3>Brand</h3>
              <Link to="/brand-os/brand-brain" className="bo-link-sm">Open brain →</Link>
            </header>
            {!brands ? (
              <LoadingCard />
            ) : brands.length === 0 ? (
              <EmptyState testId="foundation-no-brands" title="No brand yet" description="Seed one in Brand Vault." />
            ) : (
              <>
                <div className="bo-metric-row">
                  <div className="bo-metric">
                    <div className="bo-metric-label">Brands</div>
                    <div className="bo-metric-value">{brands.length}</div>
                  </div>
                  {readiness?.brand?.score !== undefined && (
                    <div className="bo-metric">
                      <div className="bo-metric-label">Readiness</div>
                      <div className="bo-metric-value">
                        <Pill tone={READINESS_TONE(readiness.brand.score)}>{readiness.brand.score}%</Pill>
                      </div>
                    </div>
                  )}
                </div>
                <ul className="bo-list-tight">
                  {brands.slice(0, 3).map((b) => (
                    <li key={b.id}>
                      <div>
                        <div className="bo-list-title">{b.name}</div>
                        {b.slug && <div className="bo-list-sub">{b.slug}</div>}
                      </div>
                      <Link to="/brand-os/vault" className="bo-link-sm">Vault</Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {/* Products */}
          <section className="bo-card" data-testid="foundation-products-card">
            <header className="bo-card-header">
              <h3>Products</h3>
              <Link to="/brand-os/product-brain" className="bo-link-sm">Open brain →</Link>
            </header>
            {!products ? (
              <LoadingCard />
            ) : products.length === 0 ? (
              <EmptyState testId="foundation-no-products" title="No products yet" description="Add products to unlock campaigns." />
            ) : (
              <>
                <div className="bo-metric-row">
                  <div className="bo-metric">
                    <div className="bo-metric-label">Products</div>
                    <div className="bo-metric-value">{products.length}</div>
                  </div>
                </div>
                <ul className="bo-list-tight">
                  {products.slice(0, 4).map((p) => (
                    <li key={p.id}>
                      <span className="bo-list-title">{p.name}</span>
                      <Link to="/brand-os/products" className="bo-link-sm">Detail</Link>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>

          {/* Audiences */}
          <section className="bo-card" data-testid="foundation-audiences-card">
            <header className="bo-card-header">
              <h3>Audiences</h3>
              <Link to="/brand-os/audience-brain" className="bo-link-sm">Open brain →</Link>
            </header>
            {!audiences ? (
              <LoadingCard />
            ) : audiences.length === 0 ? (
              <EmptyState testId="foundation-no-audiences" title="No audiences yet" description="Segment your reach in Audiences." />
            ) : (
              <>
                <div className="bo-metric-row">
                  <div className="bo-metric">
                    <div className="bo-metric-label">Audiences</div>
                    <div className="bo-metric-value">{audiences.length}</div>
                  </div>
                </div>
                <ul className="bo-list-tight">
                  {audiences.slice(0, 4).map((a) => (
                    <li key={a.id}>
                      <span className="bo-list-title">{a.name}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </div>
      </div>
    </Shell>
  );
}
